import React, { useEffect, useState } from 'react';
import {
  getHorarioDoAviso, setHorarioDoAviso, HorarioDoAviso as Horario,
} from '../services/api';

/**
 * A que horas o cutucão chega — mostrado, e sobrescrevível.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * O app passou a escolher o horário sozinho, a partir de quando ela costuma
 * lançar. Um sistema que decide sozinho e **não mostra o que decidiu** é um
 * sistema que a pessoa desliga inteiro na primeira vez que erra — ela não tem
 * como saber que dava para corrigir só o horário.
 *
 * ── Onde mora ─────────────────────────────────────────────────────────────
 *
 * Na caixa de "Desligar avisos" (22/10, pedido dela). Antes ficava solto no
 * menu do avatar; agora aparece no momento em que ela pensa em desligar, que
 * é quando "talvez seja só a hora" vale a pena ser dito.
 *
 * ── Por que a origem aparece na frase ─────────────────────────────────────
 *
 * "Às 21h" e "às 21h, porque é quando você costuma lançar" são a mesma
 * informação com confianças diferentes. E quando ainda não há histórico
 * suficiente, dizer "ainda estou aprendendo" é mais honesto que afirmar um
 * horário que saiu de três lançamentos.
 */

const HORAS = Array.from({ length: 24 }, (_, h) => h);
const doisDigitos = (h: number) => `${String(h).padStart(2, '0')}:00`;

/*
 * O horário, guardado por conta (22/10), como os modais do avatar: a caixa de
 * "Desligar avisos" abria e o seletor chegava depois, mexendo a caixa. O menu
 * do avatar já busca ao abrir, e a caixa nasce com ele.
 */
const guardados = new Map<string, Horario>();
const buscando = new Map<string, Promise<Horario>>();

export const buscarHorarioDoAviso = (userId = ''): Promise<Horario> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = getHorarioDoAviso()
    .then(h => { guardados.set(userId, h); return h; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

const HorarioDoAviso: React.FC<{ naCaixa?: boolean; userId?: string }> = ({ naCaixa = false, userId = '' }) => {
  const [horario, setHorario] = useState<Horario | null>(guardados.get(userId) ?? null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    // Falha silenciosa: é uma preferência, e nenhum erro aqui pode virar
    // alarme na tela de quem só abriu o menu.
    buscarHorarioDoAviso(userId).then(setHorario).catch(() => {});
  }, [userId]);

  if (!horario) return null;

  const trocar = async (valor: string) => {
    // '' é a opção "automático" — e ela precisa existir, senão mexer uma vez
    // prenderia a pessoa na escolha manual para sempre.
    const hora = valor === '' ? null : Number(valor);
    setSalvando(true);
    try {
      await setHorarioDoAviso(hora);
      setHorario(await buscarHorarioDoAviso(userId));
    } catch {
      /* mantém o que estava na tela */
    } finally {
      setSalvando(false);
    }
  };

  // De onde o horário saiu. A ressalva "não é um aviso diário" saiu em
  // 20/10, a pedido dela: longa demais para o menu.
  const explicacao =
    horario.origem === 'escolhida' ? 'você escolheu este horário'
    : horario.origem === 'aprendida' ? 'é quando você costuma lançar'
    : 'ainda estou aprendendo o seu horário';

  return (
    <div className={naCaixa ? 'ha-linha ha-linha--caixa' : 'ha-linha'}>
      <label className="ha-rotulo" htmlFor="ha-hora">Avisar por volta das</label>
      <select
        id="ha-hora"
        className="ha-select"
        disabled={salvando}
        value={horario.origem === 'escolhida' ? String(horario.hora) : ''}
        onChange={e => trocar(e.target.value)}
      >
        <option value="">{doisDigitos(horario.hora)} (automático)</option>
        {HORAS.map(h => (
          <option key={h} value={String(h)}>{doisDigitos(h)}</option>
        ))}
      </select>
      <span className="ha-nota">{explicacao}</span>
    </div>
  );
};

export default HorarioDoAviso;
